import React, { useCallback, useMemo, useRef } from "react";
import { stylex } from "./stylex";
import { cssVariables } from "./css_variables";
import { Text } from "./text";
import { Icon } from "./icon";
import { Spacing } from "./spacing";

type ToastId = string;
type ToastIntent = "success" | "error" | "warning";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastSettings {
  /**
   * Duration for how long the toast should stay active.
   * @default 3000
   */
  duration?: number;

  /**
   * Assign an id to the toast so you can remove it later.
   */
  id?: ToastId;

  intent?: ToastIntent;

  message: string;

  /**
   * Optional clickable action rendered on the right side of the toast.
   * Clicking dismisses the toast.
   */
  action?: ToastAction;
}

interface ToastInstance extends ToastSettings {
  /**
   * Assign an id to the toast so you can remove it later.
   */
  id: ToastId;

  /**
   * Callback invoked when the duration is up.
   */
  onRemove: () => void;
}

interface ToastContextValue {
  error: (toastSettings: ToastSettings) => ToastInstance;
  notify: (toastSettings: ToastSettings) => ToastInstance;
  success: (toastSettings: ToastSettings) => ToastInstance;
  warning: (toastSettings: ToastSettings) => ToastInstance;
  removeToast: (id: ToastId) => void;
}

const defaultToastInstance: ToastInstance = {
  id: "1",
  onRemove: () => null,
  message: "",
};

const defaultToastContext: ToastContextValue = {
  error: () => defaultToastInstance,
  notify: () => defaultToastInstance,
  removeToast: (id) => {
    return;
  },
  success: () => defaultToastInstance,
  warning: () => defaultToastInstance,
};

const ToastContext = React.createContext(defaultToastContext);

export const useToast = () => {
  return React.useContext(ToastContext);
};

const hasCustomId = (toastSettings: ToastSettings) => !!toastSettings.id;

interface ToastProviderState {
  toasts: ToastInstance[];
}

const initialState: ToastProviderState = {
  toasts: [],
};

enum ActionType {
  ADD_TOAST = "ADD_TOAST",
  REMOVE_TOAST = "REMOVE_TOAST",
}

type Action =
  | { type: ActionType.ADD_TOAST; payload: { toast: ToastInstance } }
  | { type: ActionType.REMOVE_TOAST; payload: { id: ToastId } };

const reducer = (state: ToastProviderState, action: Action) => {
  switch (action.type) {
    case ActionType.ADD_TOAST:
      return { toasts: [...state.toasts, action.payload.toast] };
    case ActionType.REMOVE_TOAST:
      return {
        toasts: state.toasts.filter((toast) => toast.id !== action.payload.id),
      };
    default:
      throw new Error("Invalid action type");
  }
};

interface ToastProviderProps {
  children?: React.ReactNode;
}

export const ToastProvider = (props: ToastProviderProps) => {
  const { children } = props;
  const idCounterRef = React.useRef(0);
  // Use reducer because we want access previous value of state
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const toastsRef = useRef(state.toasts);
  toastsRef.current = state.toasts;

  const createToastInstance = useCallback(
    (toastSettings: ToastSettings): ToastInstance => {
      const uniqueId = ++idCounterRef.current;
      const id = hasCustomId(toastSettings)
        ? `${toastSettings.id}-${uniqueId}`
        : `${uniqueId}`;

      return {
        id,
        onRemove: () =>
          dispatch({ type: ActionType.REMOVE_TOAST, payload: { id } }),
        duration: toastSettings.duration,
        intent: toastSettings.intent,
        message: toastSettings.message,
        action: toastSettings.action,
      };
    },
    []
  );

  const notify = useCallback(
    (toastSettings: ToastSettings) => {
      const toastInstance = createToastInstance(toastSettings);

      // If there's a custom toast ID passed, close existing toasts with the same custom ID
      if (hasCustomId(toastSettings)) {
        for (const toast of toastsRef.current) {
          // Since unique ID is still appended to a custom ID, skip the unique ID and check only prefix
          if (String(toast.id).startsWith(String(toastSettings.id))) {
            dispatch({
              payload: { id: toast.id },
              type: ActionType.REMOVE_TOAST,
            });
          }
        }
      }

      dispatch({
        type: ActionType.ADD_TOAST,
        payload: { toast: toastInstance },
      });

      return toastInstance;
    },
    [dispatch, createToastInstance]
  );

  const contextValue = useMemo(
    () => ({
      error: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "error" }),
      notify: (toastSettings: ToastSettings) => notify(toastSettings),
      success: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "success" }),
      warning: (toastSettings: ToastSettings) =>
        notify({ ...toastSettings, intent: "warning" }),
      removeToast: (id: ToastId) =>
        dispatch({ type: ActionType.REMOVE_TOAST, payload: { id } }),
    }),
    [dispatch, notify]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Only mount the toast container when it holds toasts. An empty element
          pinned to the top of the viewport (position: fixed; top: 0) gets
          sampled by iOS Safari for the status-bar tint, which frosts the page
          content behind the status bar while scrolling. */}
      {state.toasts.length > 0 && (
        <div data-testid="toast-container" style={styles("root")}>
          {state.toasts.map((toast) => {
            return (
              <Toast
                key={toast.id}
                id={toast.id}
                duration={toast.duration}
                intent={toast.intent}
                message={toast.message}
                action={toast.action}
                onRemove={toast.onRemove}
              />
            );
          })}
        </div>
      )}
    </ToastContext.Provider>
  );
};

interface ToastProps extends ToastInstance {}

const Toast = (props: ToastProps) => {
  const { onRemove, duration = 5000, intent, message, action } = props;

  const [offset, setOffset] = React.useState(-500);
  const dismissTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  React.useEffect(() => {
    setTimeout(() => {
      setOffset(0);
    }, 0);

    const timer = setTimeout(() => {
      setOffset(-500);
      dismissTimerRef.current = setTimeout(onRemove, 100);
    }, duration - 100);

    return () => {
      clearTimeout(timer);
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [duration, onRemove]);

  const handleActionClick = () => {
    action?.onClick();
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }
    setOffset(-500);
    dismissTimerRef.current = setTimeout(onRemove, 100);
  };

  return (
    <div
      style={{
        ...styles("toast", intent),
        transform: `translateY(${offset}px)`,
      }}
    >
      {getIconForIntent(intent)}
      <Spacing width={16} />
      <div style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
        <Text>{message}</Text>
      </div>
      {action && (
        <>
          <Spacing width={16} />
          <button
            type="button"
            onClick={handleActionClick}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              textDecoration: "underline",
              color: getActionColorForIntent(intent),
              fontWeight: 500,
            }}
          >
            {action.label}
          </button>
        </>
      )}
    </div>
  );
};

// The icon carries the intent at full strength. It is a glyph with mass to
// spare, so the bright accent reads there; the same value as text on the tint
// behind it does not, which is why the message keeps the body ink and only the
// action takes a tone of its own.
function getIconForIntent(intent?: ToastIntent) {
  switch (intent) {
    case "error":
      return <Icon name="alert-triangle" color={cssVariables.textColorError} />;
    case "success":
      return <Icon name="check-circle" color={cssVariables.textColorSuccess} />;
    default:
      return null;
  }
}

// An action on a tinted surface takes that surface's own tone, the rule the
// dashboard's status banner already follows. Brand ink on a green or pink tint
// reads as a stray element borrowed from another screen rather than as this
// toast's own way back.
//
// Hex rather than the shared --text-color-* tokens: those carry the bright
// accents that paint icons and glyphs, and they fall to ~2:1 as text on the
// tints below. These are their darkened companions, each at least 5:1 on the
// tint it pairs with. The dashboard names the same companions as tokens, out of
// reach here because this toast also ships in the customer-facing bundle, which
// never loads the dashboard stylesheet.
function getActionColorForIntent(intent?: ToastIntent) {
  switch (intent) {
    case "success":
      return "#03752c";
    case "error":
      return "#c20039";
    case "warning":
      return "#9a5b00";
    default:
      return cssVariables.textColorPrimary;
  }
}

const styles = stylex.create({
  root: {
    left: 32,
    marginBottom: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 0,
    maxWidth: 560,
    position: "fixed",
    paddingTop: 16,
    right: 32,
    top: 0,
    // Need setting zIndex for toast message higher than the header, which has zIndex of 1030 by default Bootstrap fixed-top class, so that toast message is visible.
    // Need setting zIndex for toast message higher than the modal (stripe), which has zIndex of 1055 by default Bootstrap, so that toast message is visible.
    // https://getbootstrap.com/docs/5.2/layout/z-index/
    zIndex: 1057,
  },
  toast: {
    padding: 16,
    borderRadius: cssVariables.radius,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "white",
    // The toast floats over whatever the page happens to be showing, so it
    // needs to read as a surface in front of it rather than a band painted onto
    // it — most of all the plain one, which is white on a white page and has
    // nothing but this to separate it.
    boxShadow: cssVariables.shadow,
    position: "relative",
    transition: "0.2s",
    marginBottom: 16,
  },
  error: {
    backgroundColor: "#feecf0",
  },
  success: {
    backgroundColor: "#effaf3",
  },
  warning: {
    backgroundColor: "#fffbeb",
  },
});
